import { useEffect, useMemo, useRef, useState } from 'react';
import type { OutreachSelectionResponse } from '@aegislab/shared';

/**
 * 후보 선택 상태 — IA §3-3 「선택」, 게이트 §3 불변 3.
 *
 * **선택은 초안에 남는다.** 페이지·조건이 바뀌어도 유지되므로 화면이 들고 있는 것은 서버가 준
 * 명단에 이번 화면에서 누른 것을 겹친 값이다.
 *
 * 두 갈래를 가른다. `PICKED` 는 고른 사람만 세고, `ALL_MATCHING` 은 조건에 맞는 전체에서 뺀
 * 사람만 센다(IA §3-5 원칙 ①) — 6,304명을 고르고 셋을 빼는 일을 6,301개 id 로 적지 않기
 * 위함이다. `excludedIds` 는 두 갈래 모두에서 명단을 깎는다(서버 `resolveDraftMembers` 와 같다).
 */
export interface CandidateSelection {
  mode: 'PICKED' | 'ALL_MATCHING';
  includedIds: Set<string>;
  excludedIds: Set<string>;
}

function fromServer(row: OutreachSelectionResponse | undefined): CandidateSelection {
  return {
    mode: row?.mode ?? 'PICKED',
    includedIds: new Set(row?.includedIds ?? []),
    excludedIds: new Set(row?.excludedIds ?? []),
  };
}

/** 이 후보가 지금 골라져 있나. 뺀 사람은 어느 갈래에서든 골라지지 않은 것이다. */
export function isPicked(selection: CandidateSelection, id: string): boolean {
  if (selection.excludedIds.has(id)) return false;
  return selection.mode === 'ALL_MATCHING' || selection.includedIds.has(id);
}

/**
 * 고른 사람 모드의 선택 수. 전체 선택은 `null` 이다 — 뺀 목록에 명단 밖 id(옛 조회에서 뺀 사람)가
 * 섞여 있어 화면이 명단 안 제외만 가를 수 없다. 전체 선택의 보낼 수는 「먼저 볼 것」 하단이 센다.
 */
export function selectedCountOf(selection: CandidateSelection): number | null {
  if (selection.mode === 'ALL_MATCHING') return null;
  return [...selection.includedIds].filter((id) => !selection.excludedIds.has(id)).length;
}

/**
 * 동작마다 **다음 상태를 계산해 그 값으로** `onCommit` 을 부른다. 예전에는 상태를 바꾼 뒤 화면이
 * 클로저의 옛 선택을 저장해 저장이 한 박자 늦었다(보고서 `useCandidateSelection.ts:46-47`).
 * 다음 상태는 렌더 클로저가 아니라 **마지막으로 커밋한 선택**(`latest`)에서 계산한다 — 묶음 빼기처럼
 * 응답을 기다리는 동작이 그 사이 들어온 행 해제를 덮지 않게 한다.
 *
 * 나라가 바뀌면 이 훅을 쓰는 몸통이 `key={country}` 로 다시 서서 로컬 선택이 따라오지 않는다.
 */
export function useCandidateSelection(
  saved: OutreachSelectionResponse | undefined,
  onCommit: (next: CandidateSelection) => void
) {
  const [local, setLocal] = useState<CandidateSelection | null>(null);
  const selection = useMemo(() => local ?? fromServer(saved), [local, saved]);
  const latest = useRef(selection);
  useEffect(() => {
    latest.current = selection;
  }, [selection]);

  const commit = (next: CandidateSelection) => {
    latest.current = next;
    setLocal(next);
    onCommit(next);
  };

  const toggle = (id: string) => {
    const cur = latest.current;
    const included = new Set(cur.includedIds);
    const excluded = new Set(cur.excludedIds);
    if (isPicked(cur, id)) {
      if (cur.mode === 'ALL_MATCHING') excluded.add(id);
      else included.delete(id);
    } else {
      excluded.delete(id);
      if (cur.mode === 'PICKED') included.add(id);
    }
    commit({ mode: cur.mode, includedIds: included, excludedIds: excluded });
  };

  /**
   * 표 머리줄의 모두 고르기·모두 풀기 — `toggle` 을 여럿에게 한 번에 건 것과 같고 저장은 한 번 나간다.
   * 모드는 안 바꾼다: 「이 페이지만 고르기」 와 달리 다른 페이지에서 고른 사람이 남는다.
   */
  const setPicked = (ids: readonly string[], picked: boolean) => {
    const cur = latest.current;
    const included = new Set(cur.includedIds);
    const excluded = new Set(cur.excludedIds);
    for (const id of ids) {
      if (picked) {
        excluded.delete(id);
        if (cur.mode === 'PICKED') included.add(id);
      } else if (cur.mode === 'ALL_MATCHING') excluded.add(id);
      else included.delete(id);
    }
    commit({ mode: cur.mode, includedIds: included, excludedIds: excluded });
  };

  /**
   * 「이 페이지만 고르기」 — 조건 전체를 버리고 지금 보이는 사람만 고른다(IA §3-3). 명단이 이 페이지로
   * 좁혀지므로 뺀 목록은 비운다 — 남기면 명단 밖 id 가 하단 셈을 흔든다.
   */
  const pickPage = (ids: string[]) => {
    commit({ mode: 'PICKED', includedIds: new Set(ids), excludedIds: new Set() });
  };

  /** 조건에 맞는 대상 전체 — id 를 다 받아 오지 않고 **모드를 바꾼다**. 뺀 목록은 남긴다. */
  const selectAllMatching = () => {
    commit({
      mode: 'ALL_MATCHING',
      includedIds: new Set(),
      excludedIds: new Set(latest.current.excludedIds),
    });
  };

  const exclude = (ids: readonly string[]) => {
    const excluded = new Set(latest.current.excludedIds);
    for (const id of ids) excluded.add(id);
    commit({ ...latest.current, excludedIds: excluded });
  };

  const include = (ids: readonly string[]) => {
    const excluded = new Set(latest.current.excludedIds);
    for (const id of ids) excluded.delete(id);
    commit({ ...latest.current, excludedIds: excluded });
  };

  /** 서버가 정한 명단(인원 지정 결과)을 그대로 받는다 — 저장을 다시 내지 않는다. */
  const adopt = (row: OutreachSelectionResponse) => {
    const next = fromServer(row);
    latest.current = next;
    setLocal(next);
  };

  /** 비동기 동작이 응답 뒤에 볼 최신 선택. */
  const current = () => latest.current;

  return {
    selection,
    current,
    adopt,
    toggle,
    setPicked,
    pickPage,
    selectAllMatching,
    exclude,
    include,
  };
}
